<template>
    <lti-drawer
        class="file-viewer-lti-drawer"
        :active="isOpen"
        :title="title"
        :maximized="isMaximized"
        @update:active="onActiveChange"
        @close="close"
        @state="onMaximizeChange"
    >
        <div class="file-viewer-drawer tw:flex tw:flex-col tw:h-full tw:min-h-0" data-test="file-viewer-drawer">
            <div
                v-if="contextItems.length || hasViewerControls"
                class="file-viewer-context tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1 tw:bg-blue-50 tw:dark:bg-blue-950/50 tw:px-3 tw:py-1 tw:mb-3 tw:rounded tw:shrink-0"
                data-test="file-viewer-context"
            >
                <div
                    v-if="contextItems.length"
                    class="tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1 tw:min-w-0 tw:py-1 tw:text-sm tw:text-gray-600 tw:dark:text-surface-300"
                    data-test="file-viewer-context-items"
                >
                    <template v-for="(item, index) in contextItems" :key="item.key">
                        <span
                            v-if="index > 0"
                            class="tw:text-gray-400 tw:dark:text-surface-500"
                            aria-hidden="true"
                            data-test="file-viewer-context-separator"
                        >|</span>
                        <span>
                            <span
                                class="tw:font-semibold tw:dark:text-surface-100"
                                data-test="file-viewer-context-label"
                            >{{ item.label }}:</span>
                            {{ item.value }}
                        </span>
                    </template>
                </div>

                <div
                    v-if="hasViewerControls"
                    :id="controlsTargetId"
                    class="tw:flex tw:flex-auto tw:items-center tw:justify-end"
                    data-test="file-viewer-controls-slot"
                />
            </div>

            <div
                v-if="showPreviewDisclaimer"
                class="file-viewer-preview-disclaimer tw:flex tw:items-start tw:gap-2 tw:shrink-0 tw:text-sm tw:text-gray-600 tw:bg-gray-50 tw:border tw:border-gray-200 tw:dark:text-surface-300 tw:dark:bg-surface-800 tw:dark:border-surface-700 tw:px-3 tw:py-2 tw:mb-2 tw:rounded-md"
                role="note"
                data-test="file-viewer-preview-disclaimer"
            >
                <i
                    class="pi pi-info-circle tw:text-blue-600 tw:dark:text-[#60a5fa] tw:mt-0.5 tw:shrink-0"
                    aria-hidden="true"
                />
                <span>{{ previewDisclaimerMessage }}</span>
            </div>

            <div class="file-viewer-body tw:flex-1 tw:min-h-0 tw:flex tw:flex-col tw:overflow-hidden">
                <div
                    v-if="status === VIEWER_STATUS.LOADING && !sourceUrl"
                    class="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:text-gray-500"
                    data-test="file-viewer-loading"
                >
                    <i class="pi pi-spin pi-spinner tw:text-2xl tw:mr-2" aria-hidden="true" />
                    Loading preview…
                </div>

                <div
                    v-else-if="status === VIEWER_STATUS.ERROR || status === VIEWER_STATUS.UNSUPPORTED"
                    class="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:text-center tw:px-4"
                    data-test="file-viewer-error"
                >
                    <p class="tw:text-gray-700">{{ errorMessage }}</p>
                    <a
                        v-if="downloadHref"
                        :href="downloadHref"
                        :download="context?.fileName || 'File'"
                        class="button is-primary is-outlined"
                    >
                        Download
                    </a>
                </div>

                <file-viewer-pdf
                    v-else-if="isDocumentRenderer && isPdf && sourceUrl"
                    :src="sourceUrl"
                    @ready="markReady"
                    @error="markError('document_load_failed')"
                />

                <file-viewer-docx
                    v-else-if="isDocumentRenderer && isDocx && sourceUrl"
                    :src="sourceUrl"
                    @ready="markReady"
                    @error="markError('document_load_failed')"
                />

                <file-viewer-xlsx
                    v-else-if="isDocumentRenderer && isXlsx && sourceUrl"
                    :src="sourceUrl"
                    @ready="markReady"
                    @error="markError('document_load_failed')"
                />

                <div
                    v-else-if="isDocumentRenderer"
                    class="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-3 tw:text-center tw:px-4"
                    data-test="file-viewer-document-pending"
                >
                    <p class="tw:text-gray-700">{{ documentPendingMessage }}</p>
                    <a
                        v-if="downloadHref"
                        :href="downloadHref"
                        :download="context?.fileName || 'File'"
                        class="button is-primary is-outlined"
                    >
                        Download
                    </a>
                </div>

                <file-viewer-image
                    v-else-if="isImageRenderer && sourceUrl"
                    :src="sourceUrl"
                    :alt="context?.fileName || 'Image preview'"
                    @ready="markReady"
                    @error="markError('image_load_failed')"
                />

                <file-viewer-media
                    v-else-if="isMediaRenderer && sourceUrl"
                    :src="sourceUrl"
                    :kind="capability.renderer"
                    @ready="markReady"
                    @error="markError('media_load_failed')"
                />
            </div>
        </div>
    </lti-drawer>
</template>

<script setup>
import { computed, provide, useId } from 'vue';
import FileViewerDocx from '@/components/file-viewer/FileViewerDocx.vue';
import FileViewerImage from '@/components/file-viewer/FileViewerImage.vue';
import FileViewerMedia from '@/components/file-viewer/FileViewerMedia.vue';
import FileViewerPdf from '@/components/file-viewer/FileViewerPdf.vue';
import FileViewerXlsx from '@/components/file-viewer/FileViewerXlsx.vue';
import LtiDrawer from '@/components/lti/LtiDrawer.vue';
import { FILE_VIEWER_CONTROLS_TARGET, useFileViewer } from '@/composables/useFileViewer.js';
import {
    FILE_VIEWER_DOCUMENT_PENDING_MESSAGE,
    FILE_VIEWER_ERROR_MESSAGE,
    FILE_VIEWER_PREVIEW_DISCLAIMER,
    FILE_VIEWER_RENDERERS,
    FILE_VIEWER_UNSUPPORTED_TOOLTIP,
    fileViewerShowsPreviewDisclaimer,
} from '@/utilities/fileViewerCapability.js';

const {
    isOpen,
    isMaximized,
    title,
    status,
    context,
    sourceUrl,
    downloadHref,
    capability,
    close,
    setDrawerSize,
    markReady,
    markError,
    DRAWER_SIZES,
    VIEWER_STATUS,
} = useFileViewer();

const documentPendingMessage = FILE_VIEWER_DOCUMENT_PENDING_MESSAGE;
const previewDisclaimerMessage = FILE_VIEWER_PREVIEW_DISCLAIMER;

const showPreviewDisclaimer = computed(
    () => status.value === VIEWER_STATUS.READY && fileViewerShowsPreviewDisclaimer(capability.value?.mimeType)
);

const errorMessage = computed(() => {
    if (status.value === VIEWER_STATUS.UNSUPPORTED) {
        return FILE_VIEWER_UNSUPPORTED_TOOLTIP;
    }

    return FILE_VIEWER_ERROR_MESSAGE;
});

const isDocumentRenderer = computed(() => capability.value?.renderer === FILE_VIEWER_RENDERERS.DOCUMENT);
const isPdf = computed(() => capability.value?.mimeType === 'application/pdf');
const isDocx = computed(
    () => capability.value?.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
);
const isXlsx = computed(
    () => capability.value?.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
);
const isImageRenderer = computed(() => capability.value?.renderer === FILE_VIEWER_RENDERERS.IMAGE);
const isMediaRenderer = computed(() => {
    const renderer = capability.value?.renderer;
    return renderer === FILE_VIEWER_RENDERERS.AUDIO || renderer === FILE_VIEWER_RENDERERS.VIDEO;
});

/**
 * True when the active renderer has a toolbar (page navigation and/or zoom). The
 * renderers below teleport that toolbar into the context row's controls slot, so the
 * slot must exist whenever they are mounted, including while they load and before
 * they emit ready.
 */
const hasViewerControls = computed(() => {
    const rendererMounted = status.value === VIEWER_STATUS.LOADING || status.value === VIEWER_STATUS.READY;
    if (!rendererMounted || !sourceUrl.value) {
        return false;
    }

    return (isDocumentRenderer.value && (isPdf.value || isDocx.value || isXlsx.value)) || isImageRenderer.value;
});

const controlsTargetId = `file-viewer-controls-${useId().replace(/[^\w-]/g, '-')}`;
provide(FILE_VIEWER_CONTROLS_TARGET, `#${controlsTargetId}`);

const contextItems = computed(() => {
    const ctx = context.value;
    if (!ctx) {
        return [];
    }

    return [
        { key: 'course', label: 'Course', value: ctx.courseName },
        { key: 'student', label: 'Student', value: ctx.studentName },
        { key: 'activity', label: 'Activity', value: ctx.activityName },
    ].filter(item => item.value != null && item.value !== '');
});

function onActiveChange(active) {
    if (!active) {
        close();
    }
}

function onMaximizeChange(maximized) {
    setDrawerSize(maximized ? DRAWER_SIZES.FULL : DRAWER_SIZES.PARTIAL);
}
</script>

<style>
/*
 * Shared LtiDrawer translates the header/content upward for other LTI surfaces.
 * That nudge + overflow:hidden clips the File Viewer title bar. Disable the nudge
 * and keep scrolling inside the viewer panes. The File Viewer has no footer, so the
 * bottom padding is dropped and the viewer viewport and scrollbar reach the edge.
 */
.lti-drawer.file-viewer-lti-drawer,
.lti-drawer:has(.file-viewer-drawer) {
    --lti-drawer-header-offset: 0;
    --lti-drawer-content-offset: 0;
}

.lti-drawer.file-viewer-lti-drawer .card-header,
.lti-drawer.file-viewer-lti-drawer .card-content,
.lti-drawer:has(.file-viewer-drawer) .card-header,
.lti-drawer:has(.file-viewer-drawer) .card-content {
    transform: none !important;
}

.lti-drawer.file-viewer-lti-drawer aside.card,
.lti-drawer:has(.file-viewer-drawer) aside.card {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    padding-bottom: 0 !important;
}

.lti-drawer.file-viewer-lti-drawer .card-header,
.lti-drawer:has(.file-viewer-drawer) .card-header {
    flex: 0 0 auto;
}

.lti-drawer.file-viewer-lti-drawer .card-content,
.lti-drawer:has(.file-viewer-drawer) .card-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden !important;
    padding-bottom: 0 !important;
}
</style>
